import React, { ReactElement, Fragment, useState } from "react";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import { Link } from "react-router-dom";
import { Translate } from "react-redux-i18n";
import {
  Button,
  Toolbar,
  Drawer,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import PersonIcon from "@mui/icons-material/Person";

import FontAwesomeIcon from "components/shared/FontAwesomeIcon";
import WalletAccountUtilities from "components/wallet/WalletAccountUtilities";
import { logoutRequested, toggleBalance } from "redux/auth/authSlice";
import { checkWalletPermissions, useWindowSize } from "services/utils";
import { openDialog } from "redux/dialog/dialogSlice";
import { toggleBalanceRecharge } from "redux/wallet/walletSlice";

import GamingLinks1 from "../GameLinks1";
import GamingLinks2 from "../GameLinks2";

import styles from "./styles.module.scss";
import GamingLinksv1 from "../GameLinksv1";

interface INavigation {
  skinLogo?: string;
  isRightDrawerOpen: boolean;
  setRightDrawerOpen: (arg0: boolean) => void;
  setLiveChatOpen: (state: boolean) => void;
}

export default function Navigation({
  skinLogo,
  isRightDrawerOpen,
  setRightDrawerOpen,
  setLiveChatOpen,
}: INavigation): ReactElement {
  const [width] = useWindowSize();
  const isDesktopLayout = useAppSelector(state => state.layoutToggle.isDesktopLayout)
  return (
    <Fragment>
      {
        isDesktopLayout ? <NavigationDesktop /> :
          width > 1250 ? (
            <NavigationDesktop />
          ) : (
            <NavigationMobile
              skinLogo={skinLogo}
              isRightDrawerOpen={isRightDrawerOpen}
              setRightDrawerOpen={setRightDrawerOpen}
              setLiveChatOpen={setLiveChatOpen}
            />
          )}
    </Fragment>
  );
}

function NavigationDesktop(): ReactElement {
  return <GamingLinks />;
}
function NavigationMobile({
  skinLogo,
  setRightDrawerOpen,
  isRightDrawerOpen,
  setLiveChatOpen,
}: INavigation): ReactElement {
  const dispatch = useAppDispatch();
  const [width] = useWindowSize();
  const isDesktopLayout = useAppSelector(state => state.layoutToggle.isDesktopLayout)
  const [isLeftDrawerOpen, setLeftDrawerOpen] = useState(false);

  const handleRightDrawer = () => setRightDrawerOpen(!isRightDrawerOpen);
  const handleLeftDrawer = () => setLeftDrawerOpen(!isLeftDrawerOpen);

  return (
    <Toolbar className={styles.toolbar}>
      {(width < 900 || !isDesktopLayout) && (
        <Button component={Link} to={"/home"}>
          <Box component="img" src={skinLogo} alt="" className={styles.logo} />
        </Button>
      )}
      <Drawer
        {...{
          anchor: "left",
          open: isLeftDrawerOpen,
          onClose: handleLeftDrawer,
        }}
        className={styles.drawer}
      >
        <Box>
          {" "}
          <GamingLinks handleLeftDrawer={handleLeftDrawer} />{" "}
        </Box>
      </Drawer>
      <Drawer
        {...{
          anchor: "right",
          open: isRightDrawerOpen,
          onClose: handleRightDrawer,
        }}
        PaperProps={{
          className: styles.drawerPaper,
        }}
        className={styles.drawer}
      >
        <RightDrawer
          handleRightDrawer={handleRightDrawer}
          setLiveChatOpen={setLiveChatOpen}
        />
        <Typography
          onClick={() => dispatch(openDialog({ type: "terms" }))}
          className={styles.terms}
        >
          <Translate value="content.terms" />
        </Typography>
      </Drawer>
    </Toolbar>
  );
}

interface NavLinksProps {
  handleLeftDrawer?: () => void;
}

function GamingLinks({ handleLeftDrawer }: NavLinksProps): ReactElement {
  return (
    <>
      {
        process.env.REACT_APP_SKIN_VERSION === "v1" ?
          <GamingLinksv1 />
          :
          process.env.REACT_APP_HEADER_VERSION === "version1" ? (
            <GamingLinks1 handleLeftDrawer={handleLeftDrawer} />
          ) : (
            <GamingLinks2 handleLeftDrawer={handleLeftDrawer} />
          )}
    </>
  );
}

interface RightDrawerProps {
  handleRightDrawer: () => void;
  setLiveChatOpen: (state: boolean) => void;
}

function RightDrawer({
  handleRightDrawer,
  setLiveChatOpen,
}: RightDrawerProps): ReactElement {
  const dispatch = useAppDispatch();
  const isAuth = useAppSelector((state) => state.auth.isAuth);
  const username = useAppSelector((state) => state.auth.user?.username);
  const authWallet = useAppSelector((state) => state.auth.authWallet);
  const isRegistration = useAppSelector(
    (state) => state.config.skin?.setting.registration
  );
  const isBalanceVisible = useAppSelector(
    (state) => state.auth.isBalanceVisible
  );
  const permissionsOfAuthUser = useAppSelector(
    (state) => state.auth.permissions
  );
  const isPayment = useAppSelector(
    (state) => state.config.skin?.setting.payment
  );
  const isBalanceRechargeVisible = useAppSelector(
    (state) => state.wallet.isBalanceRechargeVisible
  );
  const livechatSetting = useAppSelector(state => state.config.skin?.setting.livechat)

  const handleToggle = () => {
    dispatch(toggleBalance(!isBalanceVisible));
  };
  const handleBalanceRecharge = () => {
    dispatch(toggleBalanceRecharge());
  };
  const handleCreateUser = () => {
    dispatch(openDialog({ type: "createUser" }));
  };
  const handleLogout = () => {
    dispatch(logoutRequested());
  };
  const handleLiveChat = () => {
    setLiveChatOpen(true);
    handleRightDrawer();
  };

  return (
    <Box className={styles.rightDrawerContainer}>
      {isAuth && (
        <Box className={styles.rightDrawer}>
          <Box className={styles.profileContainer}>
            <Box className={styles.avatarContainer}>
              <Avatar className={styles.avatar}>
                <PersonIcon />
              </Avatar>
              <Typography className={styles.userName}>{username}</Typography>
            </Box>
            <Box className={styles.balanceContainer}>
              {permissionsOfAuthUser &&
                checkWalletPermissions(
                  permissionsOfAuthUser,
                  "wallet.network"
                ) && (
                  <IconButton
                    onClick={handleToggle}
                    size="small"
                    className={styles.eyeIcon}
                  >
                    {isBalanceVisible ? (
                      <FontAwesomeIcon type="fas" name="eye" />
                    ) : (
                      <FontAwesomeIcon type="fas" name="eye-slash" />
                    )}
                  </IconButton>
                )}
              {isBalanceVisible && (
                <Box>
                  <Typography className={styles.balanceValue}>
                    {/*Removed balance  if wallet.overdraft permission doesn't exist*/}
                    {permissionsOfAuthUser &&
                      checkWalletPermissions(
                        permissionsOfAuthUser,
                        "wallet.overdraft"
                      ) && (
                        <>
                          <Translate value="nav.balance" />
                          :&nbsp;
                        </>
                      )}
                    {authWallet &&
                      (
                        authWallet.balance / authWallet.currency.divider
                      ).toFixed(authWallet.currency.decimal)}{" "}
                    {authWallet && authWallet.currency.symbol}
                  </Typography>

                  {permissionsOfAuthUser &&
                    checkWalletPermissions(
                      permissionsOfAuthUser,
                      "wallet.overdraft"
                    ) && (
                      <Typography className={styles.balanceValue}>
                        <Translate value="nav.overdraft" />
                        :&nbsp;
                        {authWallet && authWallet.overdraft.toFixed(authWallet.currency.decimal)}{" "}
                        {authWallet && authWallet.currency.symbol}
                      </Typography>
                    )}
                </Box>
              )}
            </Box>
          </Box>
          <Box
            component={Link}
            to="/profile"
            onClick={handleRightDrawer}
            className={styles.menuItem}
          >
            <Box component="button" className={styles.menuButton}>
              <Box className={styles.menuImage}>
                <FontAwesomeIcon
                  type="fal"
                  name="user"
                  margin={1}
                  width="20px"
                />
              </Box>
              <Translate value="nav.profile" />
            </Box>
          </Box>
          {permissionsOfAuthUser &&
            checkWalletPermissions(permissionsOfAuthUser, "wallet.network") && (
              <Box
                component={Link}
                to="/network/manage"
                onClick={handleRightDrawer}
                className={styles.menuItem}
              >
                <Box component="button" className={styles.menuButton}>
                  <Box className={styles.menuImage}>
                    <FontAwesomeIcon
                      type="fal"
                      name="sitemap"
                      margin={1}
                      width="20px"
                    />
                  </Box>
                  <Translate value="nav.networkManage" />
                </Box>
              </Box>
            )}
          <Box
            component={Link}
            to="/reports/transactions"
            onClick={handleRightDrawer}
            className={styles.menuItem}
          >
            <Box component="button" className={styles.menuButton}>
              <Box className={styles.menuImage}>
                <FontAwesomeIcon
                  type="fal"
                  name="clipboard-list"
                  margin={1}
                  width="20px"
                />
              </Box>
              <Translate value="nav.trxList" />
            </Box>
          </Box>
          <Box
            component={Link}
            to="/reports/betting"
            onClick={handleRightDrawer}
            className={styles.menuItem}
          >
            <Box component="button" className={styles.menuButton}>
              <Box className={styles.menuImage}>
                <FontAwesomeIcon
                  type="fal"
                  name="ballot-check"
                  margin={1}
                  width="20px"
                />
              </Box>
              <Translate value="nav.betting" />
            </Box>
          </Box>
          {permissionsOfAuthUser &&
            checkWalletPermissions(
              permissionsOfAuthUser,
              "wallet.provisional_plan"
            ) && (
              <Box
                component={Link}
                to="/wallet/revenues"
                onClick={handleRightDrawer}
                className={styles.menuItem}
              >
                <Box component="button" className={styles.menuButton}>
                  <Box className={styles.menuImage}>
                    <FontAwesomeIcon
                      type="fal"
                      name="money-bill-trend-up"
                      margin={1}
                      width="20px"
                    />
                  </Box>
                  <Translate value="nav.revenue" />
                </Box>
              </Box>
            )}

          {isPayment === 1 &&
            permissionsOfAuthUser &&
            checkWalletPermissions(
              permissionsOfAuthUser,
              "wallet.recharge"
            ) && (
              <Box
                component={Link}
                to="/payments"
                onClick={handleRightDrawer}
                className={styles.menuItem}
              >
                <Box component="button" className={styles.menuButton}>
                  <Box className={styles.menuImage}>
                    <FontAwesomeIcon
                      type="fal"
                      name="battery-half"
                      margin={1}
                    />
                  </Box>
                  <Translate value="buttons.recharge" />
                </Box>
              </Box>
            )}

          {permissionsOfAuthUser &&
            checkWalletPermissions(permissionsOfAuthUser, "wallet.network") && (
              <Box onClick={handleBalanceRecharge} className={styles.menuItem}>
                <Box className={styles.menuButton}>
                  <Box className={styles.menuImage}>
                    <FontAwesomeIcon
                      type="fal"
                      name="money-bill-transfer"
                      margin={1}
                      width="20px"
                    />
                  </Box>
                  <Translate value="tooltip.transfer" />
                </Box>
              </Box>
            )}

          {isBalanceRechargeVisible && <WalletAccountUtilities />}
          {permissionsOfAuthUser &&
            checkWalletPermissions(permissionsOfAuthUser, "wallet.network") && (
              <Box onClick={handleCreateUser} className={styles.menuItem}>
                <Box className={styles.menuButton}>
                  <Box className={styles.menuImage}>
                    <FontAwesomeIcon
                      type="fal"
                      name="wallet"
                      margin={1}
                      width="20px"
                    />
                  </Box>
                  <Translate value="nav.createUser" />
                </Box>
              </Box>
            )}

        </Box>
      )}
      {process.env.REACT_APP_CHAT === "true" && isRegistration === 0 && livechatSetting === 1 && (
        <Box
          component="button"
          className={styles.logoutButton}
          onClick={handleLiveChat}
        >
          <Box className={styles.menuImage}>
            <FontAwesomeIcon type="fal" name="comment-dots" width="20px" />
          </Box>
          <Typography className={styles.logoutLabel}>LiveChat</Typography>
        </Box>
      )}
      {isAuth && (
        <Box
          component="button"
          className={styles.logoutButton}
          onClick={handleLogout}
        >
          <Box className={styles.menuImage}>
            <FontAwesomeIcon type="fal" name="sign-out-alt" width="20px" />
          </Box>
          <Typography className={styles.logoutLabel}>
            <Translate value="buttons.logout" />
          </Typography>
        </Box>
      )}

    </Box>
  );
}
